/**
 * Add Note Modal Component
 * TASK-351: Frontend Note Creation Modal
 *
 * Modal for creating new communication notes with support for:
 * - Date picker
 * - Interaction type selection (phone_call, whatsapp, in_person, other)
 * - Summary (required)
 * - Client feedback (optional)
 * - Email reminder toggle with date/time picker
 * - Validation and double-click prevention
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, MessageSquare, Phone, MessageCircle, Users, FileText, Bell, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import type { NoteSourceType, NoteCreateRequest, NoteReminder, InlineTaskCreate } from '../../../types/notes';

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (note: NoteCreateRequest) => Promise<void>;
  isLoading?: boolean;
}

const INTERACTION_TYPES: { value: NoteSourceType; label: string; icon: React.ReactNode }[] = [
  { value: 'phone_call', label: 'Phone Call', icon: <Phone size={16} /> },
  { value: 'whatsapp', label: 'WhatsApp', icon: <MessageCircle size={16} /> },
  { value: 'in_person', label: 'In-Person', icon: <Users size={16} /> },
  { value: 'other', label: 'Other', icon: <FileText size={16} /> },
];

const AddNoteModal: React.FC<AddNoteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  // Form state
  const [date, setDate] = useState('');
  const [sourceType, setSourceType] = useState<NoteSourceType>('phone_call');
  const [summary, setSummary] = useState('');
  const [clientFeedback, setClientFeedback] = useState('');
  const [enableReminder, setEnableReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('09:00');

  // TASK-352: Inline tasks state
  const [inlineTasks, setInlineTasks] = useState<InlineTaskCreate[]>([]);

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Set default date to today
      const today = new Date().toISOString().split('T')[0];
      setDate(today);
      setSourceType('phone_call');
      setSummary('');
      setClientFeedback('');
      setEnableReminder(false);
      setReminderDate('');
      setReminderTime('09:00');
      setInlineTasks([]);
      setErrors({});
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = summary.trim() !== '' || clientFeedback.trim() !== '' || inlineTasks.length > 0;
    setHasUnsavedChanges(hasChanges);
  }, [summary, clientFeedback, inlineTasks]);

  // TASK-352: Inline task handlers
  const addInlineTask = () => {
    if (inlineTasks.length >= 10) return; // Max 10 tasks
    setInlineTasks([...inlineTasks, { title: '', priority: 'medium' }]);
  };

  const updateInlineTask = (index: number, field: keyof InlineTaskCreate, value: string) => {
    const updated = [...inlineTasks];
    updated[index] = { ...updated[index], [field]: value };
    setInlineTasks(updated);
  };

  const removeInlineTask = (index: number) => {
    setInlineTasks(inlineTasks.filter((_, i) => i !== index));
  };

  // Validation
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!date) {
      newErrors.date = 'Date is required';
    }

    if (!summary.trim()) {
      newErrors.summary = 'Summary is required';
    } else if (summary.length > 500) {
      newErrors.summary = 'Summary must be 500 characters or less';
    }

    if (enableReminder) {
      if (!reminderDate) {
        newErrors.reminder = 'Reminder date is required when reminder is enabled';
      } else {
        // Check if reminder is in the past
        const reminderDateTime = new Date(`${reminderDate}T${reminderTime}`);
        if (reminderDateTime <= new Date()) {
          newErrors.reminder = 'Reminder must be set for a future date/time';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [date, summary, enableReminder, reminderDate, reminderTime]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    // Build the request payload
    const noteData: NoteCreateRequest = {
      date,
      source_type: sourceType,
      summary: summary.trim(),
    };

    // Add optional fields
    if (clientFeedback.trim()) {
      noteData.client_feedback = clientFeedback.trim();
    }

    // TASK-352: Add inline tasks (only those with non-empty titles)
    const validTasks = inlineTasks.filter((t) => t.title.trim() !== '');
    if (validTasks.length > 0) {
      noteData.inline_tasks = validTasks.map((t) => ({
        title: t.title.trim(),
        due_date: t.due_date || undefined,
        priority: t.priority || 'medium',
      }));
    }

    // Add reminder if enabled
    if (enableReminder && reminderDate) {
      const reminder: NoteReminder = {
        scheduled_at: `${reminderDate}T${reminderTime}:00`,
      };
      noteData.reminder = reminder;
    }

    await onSubmit(noteData);
  };

  // Handle close with unsaved changes warning
  const handleClose = () => {
    if (hasUnsavedChanges && !isLoading) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmed) return;
    }
    onClose();
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <MessageSquare size={20} className="text-[#002B45]" />
              <h2 className="text-lg font-semibold text-gray-900">Add Note</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    if (errors.date) setErrors((prev) => ({ ...prev, date: '' }));
                  }}
                  className={`
                    w-full px-3 py-2 border rounded-lg text-sm
                    focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    ${errors.date ? 'border-red-300' : 'border-gray-300'}
                  `}
                />
                {errors.date && (
                  <p className="mt-1 text-sm text-red-600">{errors.date}</p>
                )}
              </div>

              {/* Interaction Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interaction Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {INTERACTION_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setSourceType(type.value)}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-lg border text-sm
                        transition-colors
                        ${
                          sourceType === type.value
                            ? 'border-[#002B45] bg-[#002B45]/5 text-[#002B45]'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }
                      `}
                    >
                      {type.icon}
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={summary}
                  onChange={(e) => {
                    setSummary(e.target.value);
                    if (errors.summary) setErrors((prev) => ({ ...prev, summary: '' }));
                  }}
                  placeholder="Brief summary of the interaction..."
                  rows={3}
                  maxLength={500}
                  className={`
                    w-full px-3 py-2 border rounded-lg text-sm resize-none
                    focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    ${errors.summary ? 'border-red-300' : 'border-gray-300'}
                  `}
                />
                <div className="flex justify-between mt-1">
                  {errors.summary ? (
                    <p className="text-sm text-red-600">{errors.summary}</p>
                  ) : (
                    <span />
                  )}
                  <span className="text-xs text-gray-400">
                    {summary.length}/500
                  </span>
                </div>
              </div>

              {/* Client Feedback */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Feedback <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={clientFeedback}
                  onChange={(e) => setClientFeedback(e.target.value)}
                  placeholder="Any feedback or reaction from the client..."
                  rows={2}
                  maxLength={2000}
                  className="
                    w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none
                    focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                  "
                />
              </div>

              {/* TASK-352: Inline Tasks / Follow-up Actions */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Follow-up Actions <span className="text-gray-400">(optional)</span>
                  </label>
                  {inlineTasks.length < 10 && (
                    <button
                      type="button"
                      onClick={addInlineTask}
                      className="inline-flex items-center gap-1 text-xs text-[#002B45] hover:text-[#003d5c] font-medium"
                    >
                      <Plus size={14} />
                      Add follow-up
                    </button>
                  )}
                </div>

                {inlineTasks.length > 0 && (
                  <div className="space-y-2">
                    {inlineTasks.map((task, index) => (
                      <div key={index} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={task.title}
                            onChange={(e) => updateInlineTask(index, 'title', e.target.value)}
                            placeholder="Task description..."
                            maxLength={500}
                            className="
                              w-full px-2 py-1.5 border border-gray-200 rounded text-sm
                              focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                            "
                          />
                          <div className="flex gap-2">
                            <input
                              type="date"
                              value={task.due_date || ''}
                              onChange={(e) => updateInlineTask(index, 'due_date', e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                              className="
                                flex-1 px-2 py-1 border border-gray-200 rounded text-xs
                                focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                              "
                              placeholder="Due date"
                            />
                            <select
                              value={task.priority || 'medium'}
                              onChange={(e) => updateInlineTask(index, 'priority', e.target.value)}
                              className="
                                px-2 py-1 border border-gray-200 rounded text-xs
                                focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                              "
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="critical">Critical</option>
                            </select>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeInlineTask(index)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {inlineTasks.length === 0 && (
                  <p className="text-xs text-gray-500">
                    Add follow-up tasks to be created with this note
                  </p>
                )}
              </div>

              {/* Email Reminder Toggle */}
              <div className="border-t border-gray-100 pt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableReminder}
                    onChange={(e) => {
                      setEnableReminder(e.target.checked);
                      if (!e.target.checked) {
                        setErrors((prev) => ({ ...prev, reminder: '' }));
                      }
                    }}
                    className="
                      w-4 h-4 rounded border-gray-300 text-[#002B45]
                      focus:ring-[#F7B500] focus:ring-offset-0
                    "
                  />
                  <div className="flex items-center gap-2">
                    <Bell size={16} className="text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      Set email reminder
                    </span>
                  </div>
                </label>

                {/* Reminder Date/Time Picker */}
                {enableReminder && (
                  <div className="mt-3 pl-7 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Date</label>
                      <input
                        type="date"
                        value={reminderDate}
                        onChange={(e) => {
                          setReminderDate(e.target.value);
                          if (errors.reminder) setErrors((prev) => ({ ...prev, reminder: '' }));
                        }}
                        min={new Date().toISOString().split('T')[0]}
                        className={`
                          w-full px-2 py-1.5 border rounded text-sm
                          focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                          ${errors.reminder ? 'border-red-300' : 'border-gray-300'}
                        `}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Time</label>
                      <input
                        type="time"
                        value={reminderTime}
                        onChange={(e) => setReminderTime(e.target.value)}
                        className="
                          w-full px-2 py-1.5 border border-gray-300 rounded text-sm
                          focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                        "
                      />
                    </div>
                    {errors.reminder && (
                      <p className="col-span-2 text-sm text-red-600">{errors.reminder}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {isLoading ? 'Saving...' : 'Save Note'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddNoteModal;
